import { useEffect, useState } from 'react';

// Resolve CSS file path via the manifest (hashed filenames) with a safe fallback
const resolveCssFromManifest = async logicalName => {
  // When CRA homepage is '.', PUBLIC_URL becomes '.' which makes relative URLs.
  // For our CSS served from the app root, normalize to a root-based prefix.
  const basePrefix =
    process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.'
      ? process.env.PUBLIC_URL
      : '';
  try {
    const manifestUrl = `${basePrefix}/assets/css/manifest.json`;
    // Append a dev-time cache buster to avoid stale SW or browser caches when developing
    const url =
      process.env.NODE_ENV === 'production'
        ? manifestUrl
        : `${manifestUrl}?t=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error('manifest not available');
    const manifest = await res.json();
    return manifest[logicalName] || `${basePrefix}/assets/css/${logicalName}`;
  } catch (e) {
    // Fallback to non-hashed file if manifest not present or fetch fails
    return `${basePrefix}/assets/css/${logicalName}`;
  }
};

const useToggleStylesheet = (
  isFluid,
  isRTL,
  isDark,
  navbarStyle,
  configDispatch
) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    Array.from(document.getElementsByClassName('theme-stylesheet')).forEach(
      link => link.remove()
    );
    const link = document.createElement('link');
    const userLink = document.createElement('link');

    // Async resolve hashed file names via manifest, then inject
    (async () => {
      const themeLogical = `theme.min${isRTL ? '.rtl' : ''}.css`;
      const userLogical = `user.min${isRTL ? '.rtl' : ''}.css`;
      try {
        const [themeHref, userHref] = await Promise.all([
          resolveCssFromManifest(themeLogical),
          resolveCssFromManifest(userLogical)
        ]);

        link.href = themeHref;
        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.className = 'theme-stylesheet';

        userLink.href = userHref;
        userLink.type = 'text/css';
        userLink.rel = 'stylesheet';
        userLink.className = 'theme-stylesheet';
      } catch (_) {
        // Final fallback
        const basePrefix =
          process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.'
            ? process.env.PUBLIC_URL
            : '';
        link.href = `${basePrefix}/assets/css/${themeLogical}`;
        userLink.href = `${basePrefix}/assets/css/${userLogical}`;
      }

      link.onload = () => {
        setIsLoaded(true);
      };

      document.getElementsByTagName('head')[0].appendChild(link);
      document.getElementsByTagName('head')[0].appendChild(userLink);
    })();
    document
      .getElementsByTagName('html')[0]
      .setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  }, [isRTL]);

  useEffect(() => {
    document.documentElement.classList[isDark ? 'add' : 'remove']('dark');
    document.documentElement.classList[
      navbarStyle === 'vibrant' ? 'add' : 'remove'
    ]('vibrant');
    configDispatch({
      type: 'REFRESH'
    });
  }, [isDark, navbarStyle]);

  useEffect(() => {
    document.documentElement.classList[isFluid ? 'add' : 'remove']('fluid');
    configDispatch({
      type: 'REFRESH'
    });
  }, [isFluid]);

  return { isLoaded };
};

export default useToggleStylesheet;
